import { Column, Table as TableBase, VirtualTable } from '@devexpress/dx-react-grid';
import { Table, TableHeaderRow } from '@devexpress/dx-react-grid-material-ui';
import Tooltip from '@material-ui/core/Tooltip';
import classNames from 'classnames';
import { isNull } from 'lodash';
import moment from 'moment-timezone';
import React, { useState } from 'react';
import { deleteMedicationList } from '../../Async/MedicationListsAsync';
import { displayError, displayMessage } from '../../Config/ApplicationConfig';
import { MedicationListStatus, MedicationListType } from '../../Contracts/MedicationListsSearchFilter.Request';
import { MedicationListsSearchResult } from '../../Contracts/MedicationListsSearchResult.Response';
import { UseAsyncStatus } from '../../Hooks/useAsync';
import { MedicationLocale } from '../../Medications.en';
import { MedicationsPermissions } from '../../MedicationsPermissions';
import { getFirstErrorMessageFromApiError, isDomainValidationError } from '../../Shared/DomainValidationFunctions';
import { Nullable } from '../../Shared/Types';
import { iconLight, successGlowBorder, warningGlowBorder, white } from '../../Styles/Colours';
import { PermissionsCheck } from '../Auth/PermissionsCheck';
import { Button, ButtonMode } from '../Button';
import { DeleteDialog } from '../DeleteDialog/DeleteDialog';
import { EditIcon, LockClosedIcon, LockOpenIcon, PlusIcon, TrashCanIcon } from '../Icons';
import { LayoutCentral } from '../LayoutCentral';
import { LoadingPlaceholder } from '../Placeholder';
import { NoMedicationList } from '../SplashScreens/NoMedicationList';
import { NoResults } from '../SplashScreens/NoResults';
import { DataTable, HoverOnlyButtonGroup } from './DataTable';
import styles from './MedicationListsDataTable.module.scss';

const columnExtensions: VirtualTable.ColumnExtension[] = [
    {
        columnName: 'name'
    },
    {
        columnName: 'updatedBy'
    },
    {
        columnName: 'type',
        width: 200
    },
    {
        columnName: 'status',
        width: 150
    },
    {
        columnName: 'actions',
        align: 'right',
        width: 110
    }
];

export type MedicationListsDataTableProps = {
    filtered: boolean;
    medicationLists: MedicationListsSearchResult[];
    loadCount: number;
    limit: number;
    hasSearchString: boolean;
    loadStatus: UseAsyncStatus;
    onDelete(): void;
    onEditClick(row: MedicationListsSearchResult): void;
    onAddMedicationList?(): void;
};

const CustomStyledTableHeaderCell = (cellProps: TableHeaderRow.CellProps) => {
    return <TableHeaderRow.Cell {...cellProps} className={styles.headerCell} />;
};

const CustomStyledTableCell = (cellProps: TableBase.DataCellProps) => {
    return <Table.Cell {...cellProps} className={styles.cell} />;
};

const StatusCell = (cellProps: TableBase.DataCellProps) => {
    const status: MedicationListStatus = cellProps.tableRow.row.status;
    if (status === null) return <CustomStyledTableCell {...cellProps} />;

    let statusClass, copy, statusIcon;
    if (status === MedicationListStatus.Published) {
        statusClass = styles.published;
        copy = 'Published';
        statusIcon = <LockClosedIcon size={'16'} color={successGlowBorder} style={{ marginRight: '5px' }} />;
    } else {
        statusClass = styles.draft;
        copy = 'Draft';
        statusIcon = <LockOpenIcon size={'16'} color={warningGlowBorder} style={{ marginRight: '5px' }} />;
    }

    return (
        <CustomStyledTableCell {...cellProps}>
            <span className={classNames(styles.statusCell, statusClass)}>
                {statusIcon}
                {copy}
            </span>
        </CustomStyledTableCell>
    );
};

const NameByCell = (cellProps: TableBase.DataCellProps) => {
    const name: string = cellProps.tableRow.row.name;

    return (
        <CustomStyledTableCell {...cellProps}>
            <Tooltip title={name} placement={'bottom-start'}>
                <span>{name}</span>
            </Tooltip>
        </CustomStyledTableCell>
    );
};

const UpdatedByCell = (cellProps: TableBase.DataCellProps) => {
    const updatedBy: string = cellProps.tableRow.row.lastUpdatedBy;
    const updatedOn: string = cellProps.tableRow.row.lastUpdatedOn;
    const parsed = moment(updatedOn, 'YYYY-MM-DDThh:mm:ss.SSSSSSZ');
    const dateOutput = parsed.format('D MMMM YYYY');
    const timeOutput = parsed.format('HH:mm');

    return (
        <CustomStyledTableCell {...cellProps}>
            <Tooltip title={updatedBy} placement={'bottom-start'}>
                <span>{updatedBy}</span>
            </Tooltip>
            <div className={styles.updatedOn}>{`${dateOutput} at ${timeOutput}`}</div>
        </CustomStyledTableCell>
    );
};

const TypesCell = (cellProps: TableBase.DataCellProps) => {
    const types: MedicationListType[] = cellProps.tableRow.row.types;
    if (types === []) return <CustomStyledTableCell {...cellProps} />;

    const id: string = cellProps.tableRow.row.authoringSystemForeignKey;
    const typesListItems = types.map((item, key) => {
        if (item === MedicationListType.ScopeOfPracticeList) return <li key={`${id}1`}>Scope of practice</li>;
        if (item === MedicationListType.Formulary) return <li key={`${id}2`}>Formulary</li>;
        if (item === MedicationListType.DefaultList) return <li key={`${id}3`}>Default list</li>;
        return;
    });

    return (
        <CustomStyledTableCell {...cellProps}>
            <ul className={styles.typesList}>{typesListItems}</ul>
        </CustomStyledTableCell>
    );
};

const CustomCellComponent = (cellProps: TableBase.DataCellProps) => {
    switch (cellProps.column.name) {
        case 'name':
            return <NameByCell {...cellProps} />;
        case 'status':
            return <StatusCell {...cellProps} />;
        case 'type':
            return <TypesCell {...cellProps} />;
        case 'updatedBy':
            return <UpdatedByCell {...cellProps} />;
        default:
            return <CustomStyledTableCell {...cellProps} />;
    }
};

export function MedicationListsDataTable(props: MedicationListsDataTableProps): JSX.Element {
    const isLoading = props.loadStatus === UseAsyncStatus.Loading || props.loadStatus === UseAsyncStatus.Reloading;

    const [listToDelete, setListToDelete] = useState<Nullable<MedicationListsSearchResult>>(null);

    const columns: Column[] = [
        {
            name: 'name',
            title: 'Name',
            getCellValue: (row: MedicationListsSearchResult) => row.name
        },
        {
            name: 'updatedBy',
            title: 'Updated by',
            getCellValue: (row: MedicationListsSearchResult) => `${row.lastUpdatedBy} ${row.lastUpdatedOn}`
        },
        {
            name: 'type',
            title: 'Type',
            getCellValue: (row: MedicationListsSearchResult) => row.types.map(item => item.toString())
        },
        {
            name: 'status',
            title: 'Status',
            getCellValue: (row: MedicationListsSearchResult) => row.status
        },
        {
            name: 'actions',
            title: 'Actions',
            getCellValue: (row: MedicationListsSearchResult) => (
                <HoverOnlyButtonGroup>
                    <div className={styles.hoverOnlyButtons}>
                        <PermissionsCheck permission={MedicationsPermissions.WriteMedicationLists}>
                            {row.status !== MedicationListStatus.Published && (
                                <Button
                                    mode={ButtonMode.text}
                                    noShadowOnHover
                                    iconOnly
                                    tooltip={'Edit'}
                                    testId={'medication-list-item-edit'}
                                    onClick={e => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        props.onEditClick(row);
                                    }}
                                >
                                    <EditIcon color={iconLight} size={'20'} noPadding />
                                </Button>
                            )}

                            <Button
                                mode={ButtonMode.text}
                                noShadowOnHover
                                iconOnly
                                tooltip={'Delete'}
                                testId={'medication-list-item-delete'}
                                onClick={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setListToDelete(row);
                                }}
                            >
                                <TrashCanIcon color={iconLight} size={'20'} noPadding />
                            </Button>
                        </PermissionsCheck>
                    </div>
                </HoverOnlyButtonGroup>
            )
        }
    ];

    if (props.loadStatus === UseAsyncStatus.Loading || props.loadCount === 0) {
        return (
            <LayoutCentral>
                <LoadingPlaceholder />
            </LayoutCentral>
        );
    }

    const medicationLists: MedicationListsSearchResult[] = props.medicationLists ?? [];

    if (props.loadStatus === UseAsyncStatus.Reloading && medicationLists.length === 0) {
        return (
            <LayoutCentral>
                <LoadingPlaceholder />
            </LayoutCentral>
        );
    }

    if (props.loadStatus === UseAsyncStatus.Loaded && props.filtered && medicationLists.length === 0) {
        return (
            <LayoutCentral>
                <NoResults title={'No medication lists found. Refine your search and try again.'} />
            </LayoutCentral>
        );
    }

    const noMedicationListsInTheSystem = !props.filtered && medicationLists.length === 0;
    if (props.loadStatus === UseAsyncStatus.Loaded && noMedicationListsInTheSystem) {
        return (
            <LayoutCentral>
                <div className={styles.noResultsContainer}>
                    <NoMedicationList
                        title={
                            <span>
                                Create your first medication list
                                <br />
                                to define your default number of packs, duration, and dosage instructions.
                            </span>
                        }
                    />
                    {!!props.onAddMedicationList && (
                        <Button
                            onClick={props.onAddMedicationList}
                            testId='create-roster'
                            startIcon={<PlusIcon size='16' color={white} />}
                        >
                            Add medication list
                        </Button>
                    )}
                </div>
            </LayoutCentral>
        );
    }

    return (
        <div className={styles.dataTableContainer}>
            <DataTable<MedicationListsSearchResult>
                testId='medication-list'
                columns={columns}
                data={medicationLists}
                cellComponent={CustomCellComponent}
                headerComponent={CustomStyledTableHeaderCell}
                columnExtensions={columnExtensions}
                getRowId={(item: MedicationListsSearchResult) => item.id}
                limit={props.limit}
                loading={isLoading}
                disableColumnSorting
                rowSelection={{
                    selectedIds: [],
                    onSelect: row => {
                        props.onEditClick(row);
                    }
                }}
            />
            {!!listToDelete && (
                <DeleteDialog
                    title='Delete item'
                    testId='delete-medication-list'
                    onCancel={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        setListToDelete(null);
                    }}
                    onOk={async e => {
                        if (isNull(listToDelete)) return;
                        try {
                            e.preventDefault();
                            e.stopPropagation();
                            await deleteMedicationList(listToDelete.id);
                            displayMessage('Medication List removed');
                            props.onDelete();
                        } catch (err) {
                            let errorMessage = 'Unable to remove Medication List';
                            if (isDomainValidationError(err)) {
                                errorMessage = getFirstErrorMessageFromApiError(err, MedicationLocale);
                            }
                            displayError(errorMessage);
                        } finally {
                            setListToDelete(null);
                        }
                    }}
                >
                    Are you sure you want to remove {listToDelete?.name}?
                </DeleteDialog>
            )}
        </div>
    );
}
